// [목적] 데이터 로딩 중 플레이스홀더 Skeleton 컴포넌트 모음 — FolioCard / Profile / CollectionItem / Row 4종
// [이유] FEED-07 AC-3 — 비동기 로딩 상태에서 레이아웃 이동(CLS) 방지 + 사용자 인지 로딩 개선
// [사이드 이팩트] MUI Skeleton 래핑 — MUI 테마 및 packages/ui 토큰 radius/shadow 참조
// [수정 시 고려사항] 높이/너비 값은 대응 실제 컴포넌트 치수와 동기화 필요. 새 카드 추가 시 skeleton 도 함께 추가

import { Box, Skeleton } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

const { radius, shadow } = tokens;

// ─── FolioCardSkeleton ──────────────────────────────────────────────────────

/**
 * [목적] 폴리오 카드 스켈레톤 — 커버 + 제목 + 태그 + 작성자/좋아요 영역
 * [이유] ExplorePage / HomePage / MyFoliosPage 폴리오 그리드 로딩용
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 실제 FolioCard height(140px 커버)와 동기화
 */
export function FolioCardSkeleton(): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: `${radius.m}px`,
        overflow: 'hidden',
        boxShadow: shadow.card,
      }}
    >
      {/* 커버 영역 */}
      <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 0 }} />
      <Box sx={{ p: '12px 14px 13px' }}>
        {/* 제목 */}
        <Skeleton variant="text" height={20} sx={{ mb: '8px', borderRadius: `${radius.s}px` }} />
        {/* 태그 */}
        <Box sx={{ display: 'flex', gap: '4px', mb: '10px' }}>
          <Skeleton
            variant="rounded"
            width={52}
            height={18}
            sx={{ borderRadius: `${radius.s}px` }}
          />
          <Skeleton
            variant="rounded"
            width={44}
            height={18}
            sx={{ borderRadius: `${radius.s}px` }}
          />
          <Skeleton
            variant="rounded"
            width={60}
            height={18}
            sx={{ borderRadius: `${radius.s}px` }}
          />
        </Box>
        {/* 작성자/좋아요 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton variant="text" width={80} height={16} sx={{ borderRadius: `${radius.s}px` }} />
          <Skeleton variant="text" width={40} height={16} sx={{ borderRadius: `${radius.s}px` }} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── ProfileSkeleton ────────────────────────────────────────────────────────

/**
 * [목적] 프로필 헤더 스켈레톤 — 아바타 + 이름/handle/bio + 통계 + 액션 버튼
 * [이유] ProfilePage 상단 헤더 데이터 로딩용
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 아바타 크기(84px)는 ProfilePage 실제 아바타와 동기화
 */
export function ProfileSkeleton(): JSX.Element {
  return (
    <Box sx={{ display: 'flex', gap: '24px', alignItems: 'flex-start', mb: '30px' }}>
      {/* 아바타 */}
      <Skeleton variant="circular" width={84} height={84} sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* 이름 + handle */}
        <Box sx={{ display: 'flex', gap: '9px', mb: '5px', alignItems: 'baseline' }}>
          <Skeleton variant="text" width={120} height={28} sx={{ borderRadius: `${radius.s}px` }} />
          <Skeleton variant="text" width={80} height={18} sx={{ borderRadius: `${radius.s}px` }} />
        </Box>
        {/* bio */}
        <Skeleton variant="text" height={16} sx={{ mb: '4px', borderRadius: `${radius.s}px` }} />
        <Skeleton
          variant="text"
          width="75%"
          height={16}
          sx={{ mb: '10px', borderRadius: `${radius.s}px` }}
        />
        {/* 통계 */}
        <Box sx={{ display: 'flex', gap: '18px', mb: '14px' }}>
          <Skeleton variant="text" width={60} height={16} sx={{ borderRadius: `${radius.s}px` }} />
          <Skeleton variant="text" width={64} height={16} sx={{ borderRadius: `${radius.s}px` }} />
          <Skeleton variant="text" width={64} height={16} sx={{ borderRadius: `${radius.s}px` }} />
        </Box>
        {/* 액션 버튼 */}
        <Box sx={{ display: 'flex', gap: '6px' }}>
          <Skeleton
            variant="rounded"
            width={100}
            height={34}
            sx={{ borderRadius: `${radius.s}px` }}
          />
          <Skeleton
            variant="rounded"
            width={80}
            height={34}
            sx={{ borderRadius: `${radius.s}px` }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ─── CollectionItemSkeleton ─────────────────────────────────────────────────

/**
 * [목적] 컬렉션 아이템 행 스켈레톤 — 썸네일 + 제목 + 작성자/좋아요
 * [이유] CollectionPage 아이템 목록 무한스크롤 로딩용
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 썸네일 너비(84px)는 CollectionPage 아이템 카드와 동기화
 */
export function CollectionItemSkeleton(): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: 'background.paper',
        borderRadius: `${radius.m}px`,
        overflow: 'hidden',
        boxShadow: shadow.card,
        height: 80,
      }}
    >
      {/* 썸네일 */}
      <Skeleton
        variant="rectangular"
        width={84}
        height={80}
        sx={{ flexShrink: 0, borderRadius: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          p: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* 제목 */}
        <Skeleton variant="text" height={18} sx={{ borderRadius: `${radius.s}px` }} />
        <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: `${radius.s}px` }} />
        {/* 작성자/좋아요 */}
        <Box sx={{ display: 'flex', gap: '7px' }}>
          <Skeleton variant="text" width={72} height={14} sx={{ borderRadius: `${radius.s}px` }} />
          <Skeleton variant="text" width={40} height={14} sx={{ borderRadius: `${radius.s}px` }} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── RowSkeleton ────────────────────────────────────────────────────────────

/**
 * [목적] 일반 행 스켈레톤 — 아이콘 + 텍스트 2줄 구조 (팔로워/팔로잉 목록, 폴리오 행 공용)
 * [이유] ProfilePage 팔로워/팔로잉 탭, 폴리오 행 목록 로딩용
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 범용 행 구조 — 특수 구조는 전용 Skeleton 추가
 */
export function RowSkeleton(): JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', p: '12px 14px' }}>
      {/* 아이콘/아바타 */}
      <Skeleton variant="circular" width={44} height={44} sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton
          variant="text"
          width="70%"
          height={18}
          sx={{ mb: '4px', borderRadius: `${radius.s}px` }}
        />
        <Skeleton variant="text" width="50%" height={14} sx={{ borderRadius: `${radius.s}px` }} />
      </Box>
    </Box>
  );
}
